import { useCallback, useMemo } from "react";
import { useMe } from "../api/hooks.ts";

const STORAGE_KEY = "colorglo.franchise";

/** Exported so `useLocale` can resolve the same workshop without duplicating the storage key. */
export function storedFranchiseId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface Grant {
  readonly id: string;
  readonly name: string;
  readonly timezone: string;
  readonly permissions: readonly string[];
}

/**
 * The stored id, validated against what this user may actually reach.
 *
 * Exported and pure so the rule is testable: `localStorage` is user-writable and grants are
 * revoked server-side, so "the id we saved" and "an id this session is allowed to query" are
 * different questions. Falling back to the first grant rather than trusting the stored value
 * is what keeps a revoked franchise from producing a screen full of 403s — and, worse, from
 * putting another tenant's id into every request this app makes.
 */
export function pickFranchise<T extends Grant>(
  franchises: readonly T[],
  storedId: string | null,
): T | null {
  return franchises.find((candidate) => candidate.id === storedId) ?? franchises[0] ?? null;
}

/**
 * Which workshop the app is currently pointed at.
 *
 * Most staff have exactly one grant and never see the switcher; a country manager has
 * several and needs the calendar, the overview and the settings screens to agree on which
 * one they are looking at. That agreement is what this hook is — every screen derives the id
 * from here rather than reaching for `franchises[0]` on its own.
 *
 * Stored rather than routed for the same reason `lib/prefs.ts` is: it is which desk you are
 * sitting at, not where you are in the app, so it should survive a reload and not appear in
 * a link someone shares.
 */
export function useFranchise() {
  const me = useMe();
  const franchises = useMemo(() => me.data?.franchises ?? [], [me.data?.franchises]);
  const franchise = pickFranchise(franchises, storedFranchiseId());

  /**
   * Switching reloads. Every cached query in the app is scoped to a franchise id, and a
   * reload is the one move that cannot leave a row from the previous tenant on screen — which
   * in a franchise system is the failure worth spending a page load to rule out.
   */
  const select = useCallback((id: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      return;
    }
    window.location.reload();
  }, []);

  return {
    franchises,
    franchise,
    franchiseId: franchise?.id ?? null,
    timezone: franchise?.timezone ?? "Europe/Stockholm",
    select,
  };
}
